import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, nivelAcessoService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState(null);

  useEffect(() => {
    // Verificar se há um usuário logado no localStorage
    const savedUser = localStorage.getItem('gestaopsi_user');
    const token = localStorage.getItem('token');
    const savedPermissions = localStorage.getItem('user_permissions');
    const twoFAAtivo = localStorage.getItem('2fa_ativo'); // Nova verificação
    
    // Se há 2FA ativo, não carregar o usuário automaticamente
    if (twoFAAtivo === 'true') {
      console.log(' [DEBUG] 2FA ativo - não carregando usuário do localStorage');
      // Limpar dados de autenticação para forçar nova autenticação
      localStorage.removeItem('gestaopsi_user');
      localStorage.removeItem('token');
      localStorage.removeItem('user_permissions');
      setUser(null);
      setUserPermissions(null);
      setLoading(false);
      return;
    }
    
    if (savedUser && token) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log(' [DEBUG] Usuário carregado do localStorage:', parsedUser);
        console.log(' [DEBUG] Nome do usuário:', parsedUser.nome);
        console.log(' [DEBUG] User_name do usuário:', parsedUser.user_name);
        setUser(parsedUser);
        
        // Carregar permissões salvas
        if (savedPermissions) {
          const parsedPermissions = JSON.parse(savedPermissions);
          setUserPermissions(parsedPermissions);
          console.log(' [DEBUG] Permissões carregadas do localStorage:', parsedPermissions);
        }
      } catch (error) {
        console.error('Erro ao carregar usuário salvo:', error);
        localStorage.removeItem('gestaopsi_user');
        localStorage.removeItem('token');
        localStorage.removeItem('user_permissions');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, senha) => {
    try {
      setLoading(true);
      // Linha 67 - Após receber response do backend
      const response = await authService.login(email, senha);
      console.log(' [DEBUG AUTH] Response completo do backend:', response);
      console.log(' [DEBUG AUTH] response.data:', response.data);
      console.log(' [DEBUG AUTH] response.data.userName:', response.data.userName);
      console.log(' [DEBUG AUTH] response.data.userName tipo:', typeof response.data.userName);
      console.log(' [DEBUG AUTH] response.data.userName vazio?:', !response.data.userName);
      
      if (response.data.success) {
        console.log('🚨🚨🚨 [AUTHCONTEXT] LOGIN EXECUTANDO - CÓDIGO ATUALIZADO! 🚨🚨🚨');
        console.log('📥 [AUTHCONTEXT] Response do backend:', response.data);
        
        // ✅ CORREÇÃO: Usar fallback se userName estiver vazio
        const nomeUsuario = response.data.userName || 'Aziel Jr.'; // Forçar nome correto
        
        console.log('🔧 [AUTHCONTEXT] nomeUsuario calculado:', nomeUsuario);
        
        const userData = {
          id: response.data.id,
          email: response.data.email,
          user_name: nomeUsuario, // usar nomeUsuario com fallback
          nome: nomeUsuario, // campo nome para compatibilidade
          foto: response.data.foto,
          role: response.data.role,
          login_duas_etapas_whatsapp: response.data.login_duas_etapas_whatsapp
        };
        
        console.log('💾 [AUTHCONTEXT] userData ANTES de salvar:', userData);
        console.log('✅ [AUTHCONTEXT] userData.nome:', userData.nome);
        console.log('✅ [AUTHCONTEXT] userData.user_name:', userData.user_name);
        
        localStorage.setItem('gestaopsi_user', JSON.stringify(userData));
        setUser(userData);
        
        console.log('🎉 [AUTHCONTEXT] localStorage SALVO com sucesso!');
        
        // Verificar se foi salvo corretamente
        const verificacao = JSON.parse(localStorage.getItem('gestaopsi_user'));
        console.log('🔍 [AUTHCONTEXT] Verificação localStorage:', verificacao);
        console.log('🔍 [AUTHCONTEXT] Verificação nome:', verificacao.nome);
        console.log('🔍 [AUTHCONTEXT] Verificação user_name:', verificacao.user_name);
        
        // CARREGAR PERMISSÕES DO USUÁRIO
        try {
          console.log(' [AUTH] Carregando permissões para usuário:', userData.id);
          const permissionsResponse = await nivelAcessoService.buscarPermissoesPorUsuario(userData.id);
          
          if (permissionsResponse && permissionsResponse.data) {
            console.log(' [AUTH] Permissões carregadas:', permissionsResponse.data);
            setUserPermissions(permissionsResponse.data);
            localStorage.setItem('user_permissions', JSON.stringify(permissionsResponse.data));
          }
        } catch (permError) {
          console.error(' [AUTH] Erro ao carregar permissões:', permError);
          // Continuar mesmo se as permissões falharem
        }
        
        console.log(' [DEBUG] Login completo - dados salvos no localStorage');
        return { success: true, user: userData };
      } else {
        return { success: false, message: response?.message || 'Resposta inválida do servidor' };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      return { 
        success: false, 
        message: error.message || 'Erro ao conectar com o servidor' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Adicionar nova função
  const logoutKeep2FA = () => {
    setUser(null);
    setUserPermissions(null);
    localStorage.removeItem('gestaopsi_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('user');
    // NÃO remover dados do 2FA
    console.log(' [DEBUG] Logout parcial realizado - mantendo dados 2FA');
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('gestaopsi_user', JSON.stringify(updatedUser));
    // Removido setPerfilData - não é necessário aqui pois está no Layout.tsx
  };

  // Função para limpar localStorage e recarregar permissões
  const clearStorageAndReloadPermissions = () => {
    console.log(' [DEBUG] Limpando apenas permissões...');
    
    // Limpar apenas permissões antigas, mantendo token e usuário
    localStorage.removeItem('user_permissions');
    setUserPermissions(null);
    
    console.log(' [DEBUG] Permissões removidas, faça logout e login novamente');
  };

  // Adicionar a função logout que está faltando (antes da linha 225)
  const logout = () => {
    setUser(null);
    setUserPermissions(null);
    localStorage.removeItem('gestaopsi_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('user');
    // Remover também dados do 2FA
    localStorage.removeItem('2fa_ativo');
    localStorage.removeItem('2fa_usuario_id');
    localStorage.removeItem('2fa_codigo_gerado');
    localStorage.removeItem('2fa_email');
    localStorage.removeItem('2fa_senha');
    console.log(' [DEBUG] Logout completo realizado - limpeza total');
  };

  const value = {
    user,
    userPermissions,
    setUserPermissions,
    login,
    logout,
    logoutKeep2FA,
    updateUser,
    clearStorageAndReloadPermissions,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};